import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, ChevronDown, HelpCircle, LogOut, Menu, Search, Shield, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import ConnectionStatus from "@/components/ConnectionStatus";
import HelpPanel from "@/components/HelpPanel";
import { useAuth } from "@/context/AuthContext";
import { canAccessNavigationItem, moduleSearchItems, primaryNavigation } from "@/config/navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialNotifications = [
  {
    id: "notif-1",
    title: "Supply Chain Stock Alert",
    message: "Critical stock thresholds breached for active raw materials.",
    time: "10m ago",
    type: "critical",
    path: "/supply-chain",
    read: false,
  },
  {
    id: "notif-2",
    title: "SAP System Status",
    message: "Live SAP connection currently operating with degraded response time.",
    time: "25m ago",
    type: "warning",
    path: "/settings",
    read: false,
  },
  {
    id: "notif-3",
    title: "Production Planning Report",
    message: "Open Sales Orders requiring production order assignment loaded.",
    time: "1h ago",
    type: "info",
    path: "/planning?tab=planning-report",
    read: false,
  },
  {
    id: "notif-4",
    title: "MRP Calculation Engine",
    message: "Material Requirements Planning run completed successfully.",
    time: "2h ago",
    type: "success",
    path: "/planning?tab=materials",
    read: true,
  },
];

const SidebarContent = ({ onNavigate }) => {
  const location = useLocation();
  const { isAdmin, hasRole } = useAuth();
  const visibleItems = primaryNavigation.filter((item) => canAccessNavigationItem(item, { isAdmin, hasRole }));
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-20 items-center border-b border-slate-100 px-5">
        <Link to="/" onClick={onNavigate} className="flex h-full w-full items-center justify-center" data-testid="brand-logo"><img src="/assets/mesprosoft-logo.png" alt="MESPROSOFT" className="max-h-11 w-[150px] object-contain" /></Link>
      </div>
      <nav className="flex-1 space-y-1.5 overflow-y-auto px-3 py-5" data-testid="main-navigation">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const active = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
          return <div key={item.path}>
            <Link to={item.path} onClick={onNavigate} data-testid={item.testId} aria-current={active ? "page" : undefined} className={`flex h-11 items-center gap-3 rounded-xl px-3.5 text-sm transition-all duration-200 ${active ? "bg-blue-50/90 border border-blue-200/80 text-blue-700 font-bold shadow-xs" : "text-slate-600 hover:bg-blue-50/60 hover:text-blue-700 font-medium"}`}>
              <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? "text-blue-600" : "text-slate-500"}`} /><span>{item.label}</span>
            </Link>
          </div>;
        })}
      </nav>
      <div className="m-3 mb-4 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-blue-50/70 p-4 shadow-sm"><p className="mb-2 text-xs font-semibold text-slate-800">System Status</p><ConnectionStatus /></div>
    </div>
  );
};

const AppShell = ({ children, onRestartTour }) => {
  const { user, logout, isAdmin, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const [version, setVersion] = useState(null);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState(initialNotifications);

  useEffect(() => { axios.get(`${API}/version`).then((response) => setVersion(response.data)).catch(() => {}); }, []);
  useEffect(() => setMobileOpen(false), [location.pathname, location.search]);

  // Global Ctrl + K search shortcut listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          setSearchOpen(true);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    if (searchResults[0]) selectSearchResult(searchResults[0]);
  };

  const searchTerm = search.trim().toLowerCase();
  const searchResults = searchTerm ? moduleSearchItems
    .filter((item) => canAccessNavigationItem(item, { isAdmin, hasRole }))
    .filter((item) => [item.label, item.description, ...(item.keywords || [])].filter(Boolean).join(" ").toLowerCase().includes(searchTerm))
    .slice(0, 7) : [];

  const selectSearchResult = (item) => {
    navigate(item.path);
    setSearch("");
    setSearchOpen(false);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleNotificationClick = (notif) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
    );
    if (notif.path) {
      navigate(notif.path);
    }
  };

  return <div className="min-h-screen bg-slate-50/70">
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 border-r border-slate-200 bg-white shadow-sm lg:block"><SidebarContent /></aside>
    <div className="lg:pl-60">
      <header className="sticky top-0 z-30 h-16 border-b border-slate-200/80 bg-white/80 backdrop-blur-md shadow-xs transition-all duration-200" data-testid="main-header">
        <div className="flex h-full items-center gap-3 px-4 md:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}><SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open navigation"><Menu className="h-5 w-5" /></Button></SheetTrigger><SheetContent side="left" className="w-72 p-0"><SheetTitle className="sr-only">Application navigation</SheetTitle><SidebarContent onNavigate={() => setMobileOpen(false)} /></SheetContent></Sheet>
          <img src="/assets/mesprosoft-logo.png" alt="MESPROSOFT" className="h-8 w-28 object-contain lg:hidden" />
          {version && <span className="hidden rounded-full border border-slate-200 bg-slate-100/80 px-2.5 py-1 font-mono text-[11px] font-semibold text-slate-600 sm:inline" data-testid="version-badge">v{version.version}{version.features?.trial_balance ? " · Live SAP" : ""}</span>}

          <form onSubmit={handleSearch} className="relative ml-auto hidden w-full max-w-sm md:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              placeholder="Search modules, reports, or ask AI..."
              className="h-9.5 bg-slate-100/70 border-slate-200/80 pl-9 pr-14 text-xs shadow-inner focus-visible:bg-white focus-visible:ring-blue-500 rounded-xl"
              aria-label="Search modules"
              role="combobox"
              aria-expanded={searchOpen && Boolean(searchTerm)}
              aria-controls="module-search-results"
              aria-autocomplete="list"
            />
            <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 hidden h-5 select-none items-center gap-1 rounded border border-slate-200 bg-white px-1.5 font-mono text-[10px] font-semibold text-slate-500 shadow-xs sm:flex">
              Ctrl + K
            </kbd>
            {searchOpen && searchTerm && <div id="module-search-results" role="listbox" className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
              {searchResults.length ? searchResults.map((item) => <button key={`${item.path}-${item.label}`} type="button" role="option" className="flex w-full flex-col rounded-lg px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none" onMouseDown={(event) => { event.preventDefault(); selectSearchResult(item); }}><span className="text-sm font-medium text-slate-800">{item.label}</span>{item.description && <span className="text-xs text-slate-500">{item.description}</span>}</button>) : <p className="px-3 py-3 text-sm text-slate-500">No matching module or planning option.</p>}
            </div>}
          </form>

          <div className="ml-auto flex items-center gap-2.5 md:ml-0">
            <ConnectionStatus />
            <HelpPanel isAdmin={isAdmin} onRestartTour={onRestartTour} />

            {/* Quick Notification Bell Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-full text-slate-600 hover:bg-slate-100 hover:text-slate-900" aria-label="Notifications" title="System Notifications" data-testid="notification-bell">
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 rounded-2xl p-0 shadow-2xl border-slate-200">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 bg-slate-50/80 rounded-t-2xl">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-blue-600" />
                    <span className="font-bold text-sm text-slate-800">Notifications</span>
                    {unreadCount > 0 && (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="max-h-[340px] overflow-y-auto divide-y divide-slate-100/80 p-1">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-400">No notifications</div>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className={`flex items-start gap-3 p-3 rounded-xl text-left cursor-pointer transition-colors ${
                          !n.read ? "bg-blue-50/50 hover:bg-blue-50/90" : "hover:bg-slate-50"
                        }`}
                      >
                        <div className="mt-0.5 shrink-0">
                          {n.type === "critical" && <AlertTriangle className="h-4 w-4 text-rose-500" />}
                          {n.type === "warning" && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                          {n.type === "info" && <Info className="h-4 w-4 text-blue-500" />}
                          {n.type === "success" && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className={`text-xs ${!n.read ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>
                              {n.title}
                            </span>
                            <span className="text-[10px] text-slate-400 shrink-0">{n.time}</span>
                          </div>
                          <p className="mt-0.5 text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                            {n.message}
                          </p>
                        </div>
                        {!n.read && <span className="h-2 w-2 rounded-full bg-blue-600 shrink-0 mt-1.5" />}
                      </div>
                    ))
                  )}
                </div>

                <div className="border-t border-slate-100 p-2.5 bg-slate-50/60 rounded-b-2xl text-center">
                  <button
                    onClick={() => navigate("/overview")}
                    className="text-[11px] font-bold text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    View Executive Overview →
                  </button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Profile Pill */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9.5 gap-2 rounded-full border border-slate-200/80 bg-slate-50 px-2.5 hover:bg-slate-100/80" data-testid="user-menu-trigger">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white shadow-xs">
                    {(user?.username || "U").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="hidden flex-col text-left md:flex">
                    <span className="max-w-28 truncate text-xs font-bold text-slate-800 leading-tight">{user?.username}</span>
                    <span className="text-[10px] font-medium text-slate-500 leading-none">{isAdmin ? "Administrator" : "User"}</span>
                  </div>
                  <ChevronDown className="hidden h-3.5 w-3.5 text-slate-400 md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5 shadow-xl border-slate-200">
                <DropdownMenuLabel className="p-2">
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm text-slate-800">{user?.username}</span>
                    <span className="mt-1 flex items-center gap-1 text-xs text-slate-500 font-normal">{isAdmin && <Shield className="h-3 w-3 text-blue-600" />}{isAdmin ? "Administrator" : "User"}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onRestartTour} className="rounded-lg text-xs cursor-pointer"><HelpCircle className="mr-2 h-4 w-4 text-slate-500" />Product tour</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="rounded-lg text-xs text-rose-600 focus:text-rose-600 focus:bg-rose-50 cursor-pointer" data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" />Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6 md:py-7 lg:px-8">{children}</main>
    </div>
  </div>;
};
export default AppShell;


